import { useEffect, useRef, useCallback } from 'react';
import { initScrollTracking, initTimeTracking, trackToolUsage, trackToolResultAction } from '@/lib/analytics';

// Generate or retrieve session ID
function getSessionId(): string {
  if (typeof window === 'undefined') return '';

  const storageKey = 'tool_session_id';
  let sessionId = sessionStorage.getItem(storageKey);

  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
}

interface TrackEventOptions {
  interactionType?: 'tool_usage' | 'cta_click' | 'faq_expand' | 'glossary_click' | 'related_tool_click' | 'external_resource_click';
  interactionData?: Record<string, unknown>;
}

type ResultAction = 'copy' | 'export_pdf' | 'export_csv' | 'export_json' | 'share' | 'download' | 'print' | 'save';

export function useToolAnalytics(toolSlug: string, enabled: boolean = true) {
  const sessionId = useRef<string>('');
  const pageLoadTime = useRef<number>(0);
  const tracked = useRef<boolean>(false);

  useEffect(() => {
    // Skip if not enabled (e.g., during hydration)
    if (!enabled) return;

    // Initialize session ID
    sessionId.current = getSessionId();
    pageLoadTime.current = Date.now();

    // Track pageview (only once per mount)
    if (!tracked.current) {
      tracked.current = true;
      trackPageview();

      // Track tool page load as 'start' action in GA4
      trackToolUsage(toolSlug, 'start');
    }

    // Initialize GA4 engagement tracking
    const cleanupScroll = initScrollTracking();
    const cleanupTime = initTimeTracking('tool', toolSlug);

    // Track time on page when user leaves
    const handleBeforeUnload = () => {
      const timeOnPage = Math.floor((Date.now() - pageLoadTime.current) / 1000);
      if (timeOnPage > 0) {
        // Use sendBeacon for reliable delivery on page unload
        const data = {
          type: 'pageview',
          toolSlug,
          sessionId: sessionId.current,
          timeOnPage,
        };

        if (navigator.sendBeacon) {
          navigator.sendBeacon('/api/analytics/track', JSON.stringify(data));
        }
      }
    };

    // Track visibility change (when user switches tabs)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleBeforeUnload();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup GA4 tracking
      cleanupScroll();
      cleanupTime();

      // Cleanup existing tracking
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Send final update on unmount
      handleBeforeUnload();
    };
  }, [toolSlug, enabled]);

  const trackPageview = useCallback(async () => {
    try {
      await fetch('/api/analytics/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'pageview',
          toolSlug,
          sessionId: sessionId.current,
          referrer: document.referrer,
        }),
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Analytics tracking failed:', error);
    }
  }, [toolSlug]);

  const trackInteraction = useCallback(async (options: TrackEventOptions) => {
    try {
      await fetch('/api/analytics/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'interaction',
          toolSlug,
          sessionId: sessionId.current,
          interactionType: options.interactionType,
          interactionData: options.interactionData,
        }),
      });
    } catch (error) {
      // Silently fail
      console.debug('Analytics tracking failed:', error);
    }
  }, [toolSlug]);

  /**
   * Track when tool generates a result
   * Call this when the user clicks a primary action button (Calculate, Generate, Convert, etc.)
   */
  const trackResultGenerated = useCallback(() => {
    trackToolUsage(toolSlug, 'result_generated');
  }, [toolSlug]);

  /**
   * Track when tool completes a workflow
   * Call this for multi-step tools when user reaches the final step
   */
  const trackComplete = useCallback(() => {
    trackToolUsage(toolSlug, 'complete');
  }, [toolSlug]);

  /**
   * Track when user copies tool output
   * Call this when copy-to-clipboard is triggered
   */
  const trackCopyResult = useCallback(() => {
    trackToolResultAction(toolSlug, 'copy');
  }, [toolSlug]);

  /**
   * Track when user exports tool output
   * Call this when user downloads/exports results
   */
  const trackExportResult = useCallback((format: ResultAction) => {
    trackToolResultAction(toolSlug, format);
  }, [toolSlug]);

  return {
    trackInteraction,
    trackResultGenerated,
    trackComplete,
    trackCopyResult,
    trackExportResult,
  };
}
